import { Icon, IconName } from '@blueprintjs/core'
import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import 'browser/components/atomic-elements/organisms/entity/entity-preview-card/_entity-preview-card.scss'
import 'browser/components/json-elements/atoms/preview-card/_preview-card.scss'

interface IPreviewDetailCell {
  label?: string
  value?: string
}

/**
 * @uiComponent
 *
 * A generic, presentational summary card — `title`, `subtitle`, and `detail` cells — rendered in the
 * shared entity-preview-card style. Unlike `EntityPreviewCard` it does NOT fetch an entity; every
 * field is passed in (typically via `mapStateToProps`), so it can summarize any entity or embedded
 * object. An optional trailing `icon` + `onClick` turns the whole card into a trigger.
 *
 * Example:
 * {
 *   "type": "ui:previewCard",
 *   "mapStateToProps": { "title": "<expr>", "subtitle": "<expr>" }
 * }
 */
export interface IPreviewCardProps extends IBaseProps {
  /** Primary line (e.g. an entity type / name). */
  title?: string
  /** Secondary line under the title. */
  subtitle?: string
  /** Labeled detail cells shown below the title. */
  detail?: IPreviewDetailCell[]
  /** Trailing icon (e.g. "edit"); with `onClick` the whole card becomes a button. */
  icon?: IconName
  onClick?: () => void
}

export class PreviewCard extends React.Component<IPreviewCardProps> {
  public render() {
    const { className, style, title, subtitle, detail, icon, onClick } = this.props
    const cells = (detail || []).filter((c) => !_.isNil(c.value) && c.value !== '')
    return (
      <div
        className={classNames('c-entity-preview-card c-previewCard', className, {
          'c-entity-preview-card--isClickable': !!onClick,
        })}
        style={style}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={(e) => {
          if (onClick && e.key === 'Enter') {
            onClick()
          }
        }}
      >
        <div className="c-previewCard-body">
          {title && <div className="c-entity-preview-card--title">{title}</div>}
          {subtitle && <div className="c-previewCard-subtitle">{subtitle}</div>}
          {cells.length > 0 && (
            <div className="c-entity-preview-card--info">
              {cells.map((cell, i) => (
                <div className="c-entity-preview-card--cell" key={i}>
                  {cell.label && <div className="c-entity-preview-card--label">{cell.label}</div>}
                  <div className="c-entity-preview-card--cell-value">{cell.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        {icon && <Icon icon={icon} className="c-entity-preview-card--icon" />}
      </div>
    )
  }
}
